import {
  Component,
  input,
  signal,
  computed,
  effect,
  ChangeDetectionStrategy,
  HostListener,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { OverlayModule, ConnectedPosition } from '@angular/cdk/overlay';
import { SoTableButton } from '../so-table/so-table.interfaces';
import { SoSvgIconUI } from '../so-svg-icon/so-svg-icon.component';

@Component({
  selector: 'so-row-actions-menu-ui',
  standalone: true,
  imports: [CommonModule, OverlayModule, SoSvgIconUI],
  templateUrl: './so-row-actions-menu.component.html',
  styleUrl: './so-row-actions-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SoRowActionsMenuUI {
  actions = input.required<SoTableButton[]>();
  item = input<any>(null);
  title = input<string>('');

  protected isOpen = signal(false);

  protected overlayPositions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
  ];

  protected visibleActions = computed(() =>
    this.actions().filter(action => this.isVisible(action))
  );

  constructor() {
    // cdk-virtual-scroll-viewport recycles row component instances as the user scrolls, rebinding
    // only @Input()-bound values. If this menu was left open and its view gets recycled into a
    // different row, close it so it doesn't keep showing actions for the previous item.
    effect(() => {
      this.item();
      this.isOpen.set(false);
    }, {  });
  }

  toggleMenu(event: Event): void {
    event.stopPropagation();
    this.isOpen.update(open => !open);
  }

  closeMenu(): void {
    this.isOpen.set(false);
  }

  isVisible(action: SoTableButton): boolean {
    if (action.show === undefined) return true;
    if (typeof action.show === 'function') return action.show(this.item());
    return action.show;
  }

  isDisabled(action: SoTableButton): boolean {
    if (typeof action.disabled === 'function') return action.disabled(this.item());
    return !!action.disabled;
  }

  getTooltip(action: SoTableButton): string {
    if (typeof action.tooltip === 'function') return action.tooltip(this.item()) ?? '';
    return action.tooltip ?? '';
  }

  onActionClick(action: SoTableButton, event: Event): void {
    event.stopPropagation();
    if (this.isDisabled(action)) return;
    action.action(this.item());
    this.closeMenu();
  }

  onPanelKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Tab') return;
    this.closeMenu();
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen()) this.closeMenu();
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.isOpen()) this.closeMenu();
  }
}
